"use client";
import cn from "classnames";
import shuffle from "lodash/shuffle";
import { useMemo } from "react";
import Carousel, {
  type CarouselProps,
  type CarouselSettings,
} from "@/components/common/Carousel";
import {
  SiteBuilderModuleLayoutSize,
  SiteBuilderMultiColumnModuleDisplayOrder,
  SiteBuilderMultiColumnModuleRotation,
} from "@/constants/site-builder";
import { GridBreakpoint } from "@/constants/style";
import type { BaseSiteBuilderModule } from "@/types/site-builder";
import s from "./ColumnCarousel.module.scss";
import { getRotationSettings } from "./utils";

type Display<T> = BaseSiteBuilderModule<T>["style"]["display"];

export type ColumnCarouselProps<T> = CarouselProps<T> &
  Pick<Display<T>, "layoutSize" | "contentLayout" | "rotation"> &
  Partial<Pick<Display<T>, "displayOrder">>;

export default function ColumnCarousel<T>(props: ColumnCarouselProps<T>) {
  const {
    items,
    renderItem,
    settings: customSettings,
    contentLayout,
    layoutSize,
    rotation,
    displayOrder = SiteBuilderMultiColumnModuleDisplayOrder.AsListed,
  } = props;

  const sortedItems = useMemo(() => {
    const random =
      displayOrder === SiteBuilderMultiColumnModuleDisplayOrder.Random;
    return random ? shuffle(items) : items;
  }, [displayOrder, items]);

  const columns = contentLayout.split(":");
  const columnsLength = columns.length;
  const firstColumn = +columns[0];
  const columnsCount =
    columnsLength === 1 && firstColumn <= 6 ? firstColumn : columnsLength;
  const rotationSettings = getRotationSettings(rotation, columnsCount);

  const settings: CarouselSettings = {
    breakpoints: {
      [GridBreakpoint.sm]: {
        slidesPerView: 2,
      },
      [GridBreakpoint.lg]: {
        slidesPerView: columnsCount,
      },
      ...customSettings?.breakpoints,
    },
    ...rotationSettings,
    ...customSettings,
  };

  const className = cn(s.carousel, {
    [s.pagination]: settings.pagination,
  });

  return (
    <Carousel
      items={sortedItems}
      renderItem={renderItem}
      settings={settings}
      fullScreen={layoutSize === SiteBuilderModuleLayoutSize.FullScreen}
      smoothScrolling={rotation === SiteBuilderMultiColumnModuleRotation.Smooth}
      className={className}
    />
  );
}
